<template>
  <!--
    ChatGPTEditor wraps its fields in <form @input="updateAction">, a DELEGATED handler that writes
    action[event.target.name]. These checkboxes have no name, so without .stop every toggle would
    write a junk action[''] key — which then never matches the saved answer and leaves the editor
    permanently reporting unsaved changes. The tool selection is committed by toggle() alone.
  -->
  <div class="col-span-2" @input.stop @change.stop>
    <div class="flex items-baseline justify-between">
      <base-label id="chatGptTools"
                  label="Tools"
                  tooltip="API calls the AI may decide to make while writing this answer. It only ever sees the tools selected here."
      />
      <span v-if="tools.length" class="text-xs text-gray">{{ selectedCount }} of {{ tools.length }} selected</span>
    </div>

    <div v-if="!tools.length" class="h-10 mt-1 flex items-center">
      <span>None exist.</span>
      <router-link :to="{ name: ROUTE_NAMES.tools }" class="ml-1 underline hover:text-black">
        Create?
      </router-link>
    </div>

    <!-- PRO-740: one bordered row per tool, the selected ones highlighted; descriptions get two lines, not one -->
    <div v-else class="tools-list mt-1 flex flex-col gap-1">
      <label v-for="tool in tools"
             :key="tool.id"
             :for="`chatGptTool-${tool.id}`"
             :title="tool.description"
             class="flex items-start gap-2 px-3 py-2 rounded border border-solid"
             :class="[
               isSelected(tool.id) ? 'tool-selected border-focus' : 'border-gray-border',
               isDisabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-soft'
             ]"
      >
        <checkbox :id="`chatGptTool-${tool.id}`"
                  :is-checked="isSelected(tool.id)"
                  :is-disabled="isDisabled"
                  class="mt-0.5 flex-shrink-0"
                  @update:isChecked="toggle(tool.id, $event)"
        />
        <span class="flex flex-col min-w-0">
          <span class="text-sm font-semibold text-gray-dark break-words">{{ tool.name }}</span>
          <span v-if="tool.description" class="tool-description text-xs text-gray">{{ tool.description }}</span>
        </span>
      </label>
    </div>

    <span v-if="tools.length" class="block mt-1 text-xs text-gray">
      Every selected tool is described to the AI on each message of this answer, so select only what
      this answer needs.
    </span>
  </div>
</template>

<script>
import Checkbox from '@/components/preview/Checkbox'
import conversationModule from '@/store/modules/conversation.module'
import toolsModule from '@/store/modules/tools.module'
import { ROUTE_NAMES } from '@/router'

export default {
  name: 'ChatGPTEditorTools',

  components: { Checkbox },

  props: {
    action: {
      type: Object,
      required: true
    },
    actionId: {
      type: String,
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return { ROUTE_NAMES }
  },

  computed: {
    tools: toolsModule.tools,

    // Tools are stored on the action as ids; the assembler swaps them for full definitions at deploy.
    // An action authored before tools existed has no field at all, hence the fallback.
    selectedIds() {
      return this.action.tools || []
    },

    // Counts tools that exist — an id left behind by a deleted tool is not one
    selectedCount() {
      return this.tools.filter(tool => this.isSelected(tool.id)).length
    }
  },

  methods: {
    isSelected(toolId) {
      return this.selectedIds.includes(toolId)
    },

    toggle(toolId, isChecked) {
      if (this.isDisabled) return

      // Selection ORDER is preserved — it is the order the model sees the tools in — so a re-check
      // appends rather than restoring the original position.
      const tools = isChecked ?
        [...this.selectedIds, toolId] :
        this.selectedIds.filter(id => id !== toolId)

      conversationModule.updateChatGPTAction(this.actionId, { ...this.action, tools })
    }
  },

  mounted() {
    // The list is org-wide and small; fetching per editor keeps it fresh after a tool is added in
    // another tab, and the store already holds the result for the rest of the session.
    if (!this.tools.length) toolsModule.fetchTools()
  }
}
</script>

<style lang="scss" scoped>
// ponytail: about five rows, then the list scrolls — a long tool list would otherwise push the rest of the form away
.tools-list {
  max-height: 18rem;
  overflow-y: auto;
}

.tool-selected {
  // the focus colour at a tint light enough to keep the text readable
  background-color: rgba(45, 212, 245, 0.08);
}

.tool-description {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
